import React, { useState, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { storage } from '../services/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import styled from 'styled-components';

const StorageTestContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const TestItem = styled.li`
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 1rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  color: #dc3545;
`;

const ErrorItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid #dc3545;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const TestForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;

  input {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  button {
    padding: 0.5rem;
    background: var(--primary-color);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: #357abd;
    }
  }
`;

const StorageTest = () => {
  const { currentUser } = useAuth();
  const fileInputRef = useRef(null);
  const [testResults, setTestResults] = useState({
    images: {
      upload: { success: false, error: null },
      download: { success: false, error: null },
      delete: { success: false, error: null }
    },
    userUploads: {
      upload: { success: false, error: null },
      download: { success: false, error: null },
      delete: { success: false, error: null }
    }
  });

  const validateFile = (file, type) => {
    if (!file) {
      throw new Error('No file selected');
    }

    const maxSize = type === 'images' ? 5 * 1024 * 1024 : 2 * 1024 * 1024; // 5MB for images, 2MB for user uploads
    if (file.size > maxSize) {
      throw new Error(`File size exceeds ${maxSize / (1024 * 1024)}MB limit`);
    }

    if (type === 'images' && !file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }
  };

  const testImageStorage = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setTestResults(prev => ({
        ...prev,
        images: {
          upload: { success: false, error: 'User must be authenticated' },
          download: { success: false, error: 'User must be authenticated' },
          delete: { success: false, error: 'User must be authenticated' }
        }
      }));
      return;
    }

    try {
      const file = fileInputRef.current.files[0];
      validateFile(file, 'images');

      // Test upload
      const imageRef = ref(storage, `images/test-${Date.now()}`);
      await uploadBytes(imageRef, file);
      setTestResults(prev => ({
        ...prev,
        images: { ...prev.images, upload: { success: true, error: null } }
      }));

      // Test download
      const downloadURL = await getDownloadURL(imageRef);
      setTestResults(prev => ({
        ...prev,
        images: { ...prev.images, download: { success: true, error: null } }
      }));

      // Test delete
      await deleteObject(imageRef);
      setTestResults(prev => ({
        ...prev,
        images: { ...prev.images, delete: { success: true, error: null } }
      }));
    } catch (error) {
      console.error('Image storage test error:', error);
      setTestResults(prev => ({
        ...prev,
        images: {
          ...prev.images,
          [error.code === 'storage/unauthorized' ? 'upload' : 'download']: {
            success: false,
            error: error.message
          }
        }
      }));
    }
  };

  const testUserUploads = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setTestResults(prev => ({
        ...prev,
        userUploads: {
          upload: { success: false, error: 'User must be authenticated' },
          download: { success: false, error: 'User must be authenticated' },
          delete: { success: false, error: 'User must be authenticated' }
        }
      }));
      return;
    }

    try {
      const file = fileInputRef.current.files[0];
      validateFile(file, 'userUploads');

      // Test upload
      const uploadRef = ref(storage, `users/${currentUser.uid}/test-${Date.now()}`);
      await uploadBytes(uploadRef, file);
      setTestResults(prev => ({
        ...prev,
        userUploads: { ...prev.userUploads, upload: { success: true, error: null } }
      }));

      // Test download
      const downloadURL = await getDownloadURL(uploadRef);
      setTestResults(prev => ({
        ...prev,
        userUploads: { ...prev.userUploads, download: { success: true, error: null } }
      }));

      // Test delete
      await deleteObject(uploadRef);
      setTestResults(prev => ({
        ...prev,
        userUploads: { ...prev.userUploads, delete: { success: true, error: null } }
      }));
    } catch (error) {
      console.error('User uploads test error:', error);
      setTestResults(prev => ({
        ...prev,
        userUploads: {
          ...prev.userUploads,
          [error.code === 'storage/unauthorized' ? 'upload' : 'download']: {
            success: false,
            error: error.message
          }
        }
      }));
    }
  };

  return (
    <StorageTestContainer>
      <TestSection>
        <h3>Storage Security Rules Tests</h3>
        
        <TestForm onSubmit={testImageStorage}>
          <input 
            type="file" 
            ref={fileInputRef} 
            accept="image/*"
            onChange={() => {
              // Reset test results when file changes
              setTestResults(prev => ({
                ...prev,
                images: {
                  upload: { success: false, error: null },
                  download: { success: false, error: null },
                  delete: { success: false, error: null }
                }
              }));
            }}
          />
          <button type="submit">Test Images Storage</button>
        </TestForm>

        <TestForm onSubmit={testUserUploads}>
          <input 
            type="file" 
            ref={fileInputRef}
            onChange={() => {
              // Reset test results when file changes
              setTestResults(prev => ({
                ...prev,
                userUploads: {
                  upload: { success: false, error: null },
                  download: { success: false, error: null },
                  delete: { success: false, error: null }
                }
              }));
            }}
          />
          <button type="submit">Test User Uploads</button>
        </TestForm>

        <TestResults>
          <h3>Test Results</h3>
          <TestList>
            <TestItem>Images Upload: {testResults.images.upload.success ? '✅' : '❌'}</TestItem>
            <TestItem>Images Download: {testResults.images.download.success ? '✅' : '❌'}</TestItem>
            <TestItem>Images Delete: {testResults.images.delete.success ? '✅' : '❌'}</TestItem>
            <TestItem>User Uploads Upload: {testResults.userUploads.upload.success ? '✅' : '❌'}</TestItem>
            <TestItem>User Uploads Download: {testResults.userUploads.download.success ? '✅' : '❌'}</TestItem>
            <TestItem>User Uploads Delete: {testResults.userUploads.delete.success ? '✅' : '❌'}</TestItem>
          </TestList>

          {(Object.values(testResults).some(collection => 
            Object.values(collection).some(test => test.error)
          )) && (
            <ErrorList>
              {testResults.images.upload.error && <ErrorItem>Images Upload: {testResults.images.upload.error}</ErrorItem>}
              {testResults.images.download.error && <ErrorItem>Images Download: {testResults.images.download.error}</ErrorItem>}
              {testResults.images.delete.error && <ErrorItem>Images Delete: {testResults.images.delete.error}</ErrorItem>}
              {testResults.userUploads.upload.error && <ErrorItem>User Uploads Upload: {testResults.userUploads.upload.error}</ErrorItem>}
              {testResults.userUploads.download.error && <ErrorItem>User Uploads Download: {testResults.userUploads.download.error}</ErrorItem>}
              {testResults.userUploads.delete.error && <ErrorItem>User Uploads Delete: {testResults.userUploads.delete.error}</ErrorItem>}
            </ErrorList>
          )}
        </TestResults>
      </TestSection>
    </StorageTestContainer>
  );
};

export default StorageTest; 